import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Menu, X } from 'lucide-react'
import logoImage from '@/assets/logo.png'

interface HeaderProps {
  currentPage?: string
  ctaText?: string
  ctaLink?: string
}

export function Header({ currentPage, ctaText, ctaLink }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const navigationLinks = [
    { to: '/', label: 'Home', page: 'home' },
    { to: '/about', label: 'About', page: 'about' },
    { to: '/services', label: 'Services', page: 'services' },
    { to: '/gallery', label: 'Gallery', page: 'gallery' },
    { to: '/testimonials', label: 'Testimonials', page: 'testimonials' },
    { to: '/blog', label: 'Blog', page: 'blog' },
    { to: '/contact', label: 'Contact', page: 'contact' }
  ]

  return (
    <nav className="bg-black shadow-2xl border-b border-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 backdrop-blur-sm sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 hover:scale-105 transition-all duration-300">
              <img 
                src={logoImage} 
                alt="Sri Sai Sabari Dental Clinic Logo" 
                className="h-10 w-10 md:h-12 md:w-12"
              />
              <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent hover:from-yellow-300 hover:via-yellow-400 hover:to-yellow-500 transition-all duration-300">
                Sri Sai Sabari Dental Clinic
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {navigationLinks.map((link) => (
              <Link 
                key={link.page}
                to={link.to} 
                className={`${currentPage === link.page ? 'text-white' : 'text-gray-300'} hover:bg-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 hover:bg-clip-text hover:text-transparent transition-all duration-300 hover:scale-110 relative group`}
              >
                <span>{link.label}</span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}
          </div>

          {/* Desktop CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Button asChild className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-yellow-500/25">
              <Link to={ctaLink || '/book-appointment'}>{ctaText || 'Book Appointment'}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-white p-2 rounded-md hover:bg-gray-800 transition-all duration-300"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-900 border-t border-yellow-500">
              {navigationLinks.map((link) => (
                <Link
                  key={link.page}
                  to={link.to}
                  className={`${
                    currentPage === link.page 
                      ? 'bg-yellow-500 text-black' 
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  } block px-3 py-2 rounded-md text-base font-medium transition-all duration-300`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 pb-2">
                <Button asChild className="w-full bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold">
                  <Link to={ctaLink || '/book-appointment'} onClick={() => setIsMenuOpen(false)}>
                    {ctaText || 'Book Appointment'}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}